import { useEffect, useRef, useState } from "react";
import useClickOutside from "../../hooks/useClickOutside";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSetChatModel } from "../../api/queryHooks";
import useHeaderSelectedAIModel from "../../store/useHeaderSelectedAIModel";
import useModelStore from "../../store/useModelStore";
import UserAuthStore from "../../store/userStore";
import useResolvedModel from "../../hooks/useResolvedModel";
import { useSparkContext } from "../../context/SparkContext";

const PromptModelSelector = ({ compact = false, initialModelId = null, onModelSelect = null }) => {
  const { chat_id, space_id, space } = useSparkContext();
  const dropdownRef = useRef();
  const buttonRef = useRef();
  const localResolvedModelIdRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [localSelectedModel, setLocalSelectedModel] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, bottom: 0, left: 0, openUpward: true });

  const selectedModel = useHeaderSelectedAIModel((state) => state.selectedModel);
  const setSelectedModel = useHeaderSelectedAIModel((state) => state.setSelectedModel);
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);

  const modelSpace = space || (space_id ? { space_id } : null);
  const { resolvedModel, isLoading, options, chatExists } = useResolvedModel({
    chat_id,
    space: modelSpace,
    initialModelId,
  });
  const { mutate } = useSetChatModel();

  useClickOutside([dropdownRef, buttonRef], () => setIsOpen(false));

  // Sync derived model into the imperative store so generate.js and other consumers see it
  useEffect(() => {
    if (resolvedModel) {
      if (onModelSelect) {
        if (localResolvedModelIdRef.current !== resolvedModel.id) {
          localResolvedModelIdRef.current = resolvedModel.id;
          setLocalSelectedModel(resolvedModel);
          onModelSelect(resolvedModel);
        }
      } else {
        setSelectedModel(resolvedModel);
      }
    }
  }, [resolvedModel, onModelSelect, setSelectedModel]);

  const displayedModel = onModelSelect ? localSelectedModel : selectedModel;

  // Update dropdown position when opening — flip downward if near top of viewport
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const DROPDOWN_MAX_HEIGHT = 320; // matches max-h-80
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceAbove >= DROPDOWN_MAX_HEIGHT || spaceAbove > spaceBelow;
      setDropdownPosition({
        top: rect.bottom,
        bottom: window.innerHeight - rect.top,
        left: rect.left,
        openUpward,
      });
    }
  }, [isOpen]);

  const handleModelSelect = (model) => {
    if (onModelSelect) {
      setLocalSelectedModel(model);
      onModelSelect(model);
      setIsOpen(false);
      return;
    }
    if (chat_id) {
      // Record user override per chat so the hook picks it up on next render.
      // Done unconditionally so the next generation uses the chosen model even
      // before it is persisted to the backend.
      useModelStore.getState().setSelectedModel(chat_id, model.id);
      // Persist to backend ONLY once the chat document exists. On a brand-new
      // chat the chat_id is assigned client-side before the Firestore spark doc
      // is created (that happens on first message send), so set_spark_model would
      // return 400. generate.js's setChatModelWithRetry persists the model after
      // the doc is created, so nothing is lost by skipping the call here.
      if (chatExists) {
        mutate({
          model_id: model.id,
          chat_id: chat_id,
          org_id: orgId,
          space_id: space_id || null,
          ...(space_id
            ? { owner_id: space?.owner_id }
            : { user_id: user?.uid }),
        });
      }
    } else {
      // No chat yet (new conversation) — write directly to the imperative store
      setSelectedModel(model);
    }
    setIsOpen(false);
  };

  if (isLoading || !options || options.length === 0) return null;

  return (
    <>
      <div className="relative">
        <button
          type="button"
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Select model"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className={`flex items-center gap-1.5 rounded-md transition-colors duration-200 font-medium min-w-0 bg-fill-navigation-hover dark:bg-fill-navigation-active hover:bg-fill-gray-200 dark:hover:bg-fill-sidebar-surface-dark text-ink-gray-700 dark:text-ink-gray-200 ${compact ? "px-2.5 py-1.5 text-[12px]" : "px-3 py-2 text-xs gap-2"}`}
        >
          <span className={`truncate ${compact ? "max-w-[90px]" : "max-w-[100px]"}`}>
            {displayedModel ? displayedModel.name : "Select Model"}
          </span>
          <ChevronDown
            className={`w-3 h-3 transition-transform duration-200 flex-shrink-0 ${
              isOpen ? "rotate-180" : "rotate-0"
            }`}
          />
        </button>
      </div>

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={dropdownRef}
              role="listbox"
              data-edit-toolbar-portal
              initial={{ opacity: 0, y: dropdownPosition.openUpward ? 10 : -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: dropdownPosition.openUpward ? 10 : -10 }}
              transition={{ duration: 0.15 }}
              style={{
                position: 'fixed',
                ...(dropdownPosition.openUpward
                  ? { bottom: `${dropdownPosition.bottom + 4}px` }
                  : { top: `${dropdownPosition.top + 4}px` }),
                left: `${dropdownPosition.left}px`,
                width: '260px',
                zIndex: 9999,
              }}
              className="overflow-hidden bg-fill-white dark:bg-fill-panel-surface border border-edge-gray-200 dark:border-edge-panel-border rounded-xl shadow-lg backdrop-blur-sm"
            >
              <div className="px-4 py-3 text-[13px] text-ink-gray-700 dark:text-ink-gray-200 font-semibold border-b border-edge-border dark:border-edge-muted-control bg-fill-white dark:bg-fill-navigation-surface-dark/100">
                Select Model
              </div>
              <div className="max-h-[220px] overflow-y-auto show-scrollbar p-2 flex flex-col gap-1">
                {options.map((option) => {
                  const isSelected = displayedModel?.id === option.id;
                  return (
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      key={option.id}
                      onClick={() => handleModelSelect(option)}
                      className={`min-h-[50px] w-full flex items-center px-3 py-1 text-left rounded-lg cursor-pointer group relative transition-all duration-200
                        ${
                          isSelected
                            ? "photon-control-active bg-fill-navigation-active border border-edge-indigo-100 dark:border-edge-gray-600"
                            : "hover:bg-fill-gray-200 dark:hover:bg-fill-gray-700"
                        }`}
                    >
                      <div className="flex flex-col w-full justify-center">
                        <span
                          className={`truncate text-[12px] font-medium ${
                            isSelected
                              ? "text-inherit font-semibold"
                              : "text-ink-gray-900 dark:text-ink-gray-50"
                          }`}
                        >
                          {option.name}
                        </span>
                        {option.description && (
                          <span className="text-ink-gray-500 dark:text-ink-gray-400 text-[10px] line-clamp-2 break-words leading-3 mt-1">
                            {option.description}
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <div className="flex items-center flex-shrink-0 ml-2">
                          <Check className="w-3.5 h-3.5 text-ink-primary dark:text-ink-accent" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default PromptModelSelector;